import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { apiClient } from '../../api'
import { AnswerFeedback } from '../../types/chatTypes'

export const useMessageFeedback = (messageId: string) => {
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const queryClient = useQueryClient()

  // Skip feedback for temporary messages (those with IDs starting with "temp")
  const shouldEnableFeedback = !!messageId && !messageId.startsWith('temp')

  // Query key for this specific message feedback
  const feedbackQueryKey = ['messageFeedback', messageId]

  // Fetch message feedback data
  const { data: feedback, isLoading } = useQuery({
    queryKey: feedbackQueryKey,
    queryFn: () => apiClient.message.fetchMessageFeedback(messageId),
    // Only fetch if we have a valid messageId that's not temporary
    enabled: shouldEnableFeedback,
    // Don't refetch on window focus for feedback state
    refetchOnWindowFocus: false,
  })

  // Derived feedback state
  const isLiked = feedback?.is_good_response || false
  const isDisliked = feedback?.is_bad_response || false

  // Mutation for updating message feedback
  const { mutate: updateFeedback } = useMutation({
    mutationFn: (feedbackData: {
      is_good_response: boolean
      is_bad_response: boolean
      reason_bad_response?: string
      feedback_bad_response?: string
    }) => apiClient.message.updateMessageFeedback(messageId, feedbackData),
    // Use optimistic updates
    onMutate: async (newFeedback) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: feedbackQueryKey })

      // Snapshot the previous value
      const previousFeedback = queryClient.getQueryData(feedbackQueryKey)

      // Optimistically update to the new value
      queryClient.setQueryData(feedbackQueryKey, {
        ...feedback,
        ...newFeedback
      })

      // Return a context object with the previous value
      return { previousFeedback }
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, newFeedback, context) => {
      queryClient.setQueryData(feedbackQueryKey, context?.previousFeedback)
    },
    // After success or failure, refetch to ensure data consistency
    onSettled: () => {
      if (shouldEnableFeedback) {
        queryClient.invalidateQueries({ queryKey: feedbackQueryKey })
      }
    }
  })

  const likeMessage = () => {
    if (!shouldEnableFeedback) return

    const newLikedState = !isLiked

    updateFeedback({
      is_bad_response: false,
      is_good_response: newLikedState,
      reason_bad_response: '',
      feedback_bad_response: '',
    })
  }

  const dislikeMessage = () => {
    if (!shouldEnableFeedback) return

    if (isDisliked) {
      // If already disliked, undo the dislike
      updateFeedback({
        is_bad_response: false,
        is_good_response: false,
        reason_bad_response: '',
        feedback_bad_response: '',
      })
    } else {
      // Open feedback modal for dislike
      setIsFeedbackModalOpen(true)
    }
  }

  const closeFeedbackModal = () => {
    setIsFeedbackModalOpen(false)
  }

  const submitFeedback = (answerFeedback: AnswerFeedback) => {
    if (!shouldEnableFeedback) return

    updateFeedback({
      is_bad_response: true,
      is_good_response: false,
      reason_bad_response: answerFeedback.reason,
      feedback_bad_response: answerFeedback.feedback,
    })

    setIsFeedbackModalOpen(false)
  }

  const resetFeedback = () => {
    if (!shouldEnableFeedback) return

    updateFeedback({
      is_bad_response: false,
      is_good_response: false,
      reason_bad_response: '',
      feedback_bad_response: '',
    })
  }

  return {
    isLiked,
    isDisliked,
    isFeedbackModalOpen,
    likeMessage,
    dislikeMessage,
    closeFeedbackModal,
    submitFeedback,
    resetFeedback,
    isLoading
  }
}
